import React, { useState, useEffect } from 'react';
import { Modal, Table, Button, Form, Row, Col, Badge, Dropdown } from 'react-bootstrap';
import { checkService } from '../../services/api';
import { toast } from 'react-toastify';

interface CheckListProps {
  show: boolean;
  onHide: () => void;
  checkbook: any;
}

const CheckList: React.FC<CheckListProps> = ({ show, onHide, checkbook }) => {
  const [checks, setChecks] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  const fetchChecks = async () => {
    if (!checkbook) return;
    
    try {
      setLoading(true);
      const params: any = { checkbook_id: checkbook.id };
      if (statusFilter) params.status = statusFilter;

      const response = await checkService.getAll(params);
      setChecks(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des chèques');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show && checkbook) {
      fetchChecks();
    }
  }, [show, checkbook, statusFilter]);

  const handleStatusChange = async (check: any, newStatus: string) => {
    try {
      await checkService.updateStatus(check.id, newStatus);
      toast.success('Statut mis à jour avec succès');
      fetchChecks();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      unused: { bg: 'secondary', text: 'Non utilisé' },
      used: { bg: 'primary', text: 'Utilisé' },
      cancelled: { bg: 'danger', text: 'Annulé' },
      cashed: { bg: 'success', text: 'Encaissé' },
      rejected: { bg: 'warning', text: 'Rejeté' },
      caution: { bg: 'info', text: 'Caution' },
    };
    const config = statusMap[status as keyof typeof statusMap] || { bg: 'secondary', text: status };
    return <Badge bg={config.bg}>{config.text}</Badge>;
  };

  const getStatusOptions = (currentStatus: string) => {
    const allStatuses = [
      { value: 'unused', label: 'Non utilisé' },
      { value: 'used', label: 'Utilisé' },
      { value: 'cancelled', label: 'Annulé' },
      { value: 'cashed', label: 'Encaissé' },
      { value: 'rejected', label: 'Rejeté' },
      { value: 'caution', label: 'Caution' },
    ];
    return allStatuses.filter(status => status.value !== currentStatus);
  };

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          Chèques du carnet: {checkbook?.name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-3">
          <Col md={4}>
            <Form.Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">Tous les statuts</option>
              <option value="unused">Non utilisé</option>
              <option value="used">Utilisé</option>
              <option value="cancelled">Annulé</option>
              <option value="cashed">Encaissé</option>
              <option value="rejected">Rejeté</option>
            </Form.Select>
          </Col>
          <Col md={8}>
            <div className="d-flex gap-3">
              <div><strong>Total:</strong> {checkbook?.total_checks}</div>
              <div><strong>Utilisés:</strong> {checkbook?.used_checks_count}</div>
              <div><strong>Disponibles:</strong> {checkbook?.unused_checks_count}</div>
            </div>
          </Col>
        </Row>

        {loading ? (
          <div className="text-center">Chargement...</div>
        ) : (
          <Table striped bordered hover responsive size="sm">
            <thead>
              <tr>
                <th>N° Chèque</th>
                <th>Statut</th>
                <th>Montant</th>
                <th>Bénéficiaire</th>
                <th>Date émission</th>
                <th>Date encaissement</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {(checks?.data?.data || checks?.data || []).map((check: any) => (
                <tr key={check.id}>
                  <td>{check.check_number}</td>
                  <td>
                    <Dropdown>
                      <Dropdown.Toggle variant="link" size="sm" className="p-0">
                        {getStatusBadge(check.status)}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {getStatusOptions(check.status).map((status) => (
                          <Dropdown.Item
                            key={status.value}
                            onClick={() => handleStatusChange(check, status.value)}
                          >
                            {status.label}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                  <td>
                    {check.amount ? 
                      new Intl.NumberFormat('fr-FR', {
                        style: 'currency',
                        currency: 'MAD'
                      }).format(check.amount) : '-'
                    }
                  </td>
                  <td>{check.payee || '-'}</td>
                  <td>
                    {check.issue_date ? 
                      new Date(check.issue_date).toLocaleDateString('fr-FR') : '-'
                    }
                  </td>
                  <td>
                    {check.cash_date ? 
                      new Date(check.cash_date).toLocaleDateString('fr-FR') : '-'
                    }
                  </td>
                  <td>{check.notes || '-'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fermer
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CheckList;